import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Typography,
  InputAdornment,
  TextField,
  IconButton,
  Box,
  FormControl,
  FormHelperText,
  Slide,
  useTheme,
  Autocomplete,
  Chip,
  CircularProgress,
  Alert
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import FlagIcon from "@mui/icons-material/Flag";
import redFocusStyles from "../../../../components/ui/formUI/RedFocusedStyleV2";
import { activateGameBundle, getOrderStatus } from "../../../../services/gameService";

const ORDER_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  PENDING: 'pending'
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiBackdrop-root": {
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(0, 0, 0, 0.4)",
  },
  "& .MuiPaper-root": {
    border: `1px solid ${theme.palette.mode === "dark" ? "#555" : "#d32f2f"}`,
    borderRadius: 16,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    width: "500px",
    maxWidth: "90vw",
    boxShadow: theme.shadows[10],
  },
}));

const countries = [
  { code: "AF", name: "Afghanistan", dialCode: "+93", flag: "🇦🇫" },
  { code: "US", name: "United States", dialCode: "+1", flag: "🇺🇸" },
  { code: "UK", name: "United Kingdom", dialCode: "+44", flag: "🇬🇧" },
  { code: "IN", name: "India", dialCode: "+91", flag: "🇮🇳" },
  { code: "CA", name: "Canada", dialCode: "+1", flag: "🇨🇦" },
  { code: "AU", name: "Australia", dialCode: "+61", flag: "🇦🇺" },
  { code: "DE", name: "Germany", dialCode: "+49", flag: "🇩🇪" },
  { code: "FR", name: "France", dialCode: "+33", flag: "🇫🇷" },
];

export default function ActivateGameModal({
  open,
  onClose,
  game,
  playerId,
  setPlayerId,
}) {
  const theme = useTheme();
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [playerIdError, setPlayerIdError] = useState("");
  const [loading, setLoading] = useState(false);
  const [activationStatus, setActivationStatus] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedCountry(countries[0]);
      setPlayerId("");
      setPlayerIdError("");
      setActivationStatus(null);
      setOrderDetails(null);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    }
  }, [open]);

  const handlePlayerIdChange = (e) => {
    const input = e.target.value;
    setPlayerId(input);
    
    if (input && input.length < 3) {
      setPlayerIdError("Player ID must be at least 3 characters");
    } else {
      setPlayerIdError("");
    }
  };

  const startPollingOrderStatus = async (orderId) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    let pollCount = 0;
    const maxPolls = 60;

    const interval = setInterval(async () => {
      try {
        pollCount++;
        const statusResponse = await getOrderStatus(orderId);
        const currentStatus = statusResponse.data?.status;
        
        console.log(`Poll ${pollCount}: Game order status:`, currentStatus);
        
        setActivationStatus(currentStatus);
        setOrderDetails(prev => ({
          ...prev,
          ...statusResponse.data
        }));

        if (currentStatus === ORDER_STATUS.SUCCEEDED || 
            currentStatus === ORDER_STATUS.FAILED || 
            pollCount >= maxPolls) {
          clearInterval(interval);
          setPollingInterval(null);
          
          if (pollCount >= maxPolls) {
            setActivationStatus(ORDER_STATUS.FAILED);
          }
        }
      } catch (error) {
        console.error("Error polling game order status:", error);
        if (pollCount >= maxPolls) {
          clearInterval(interval);
          setPollingInterval(null);
          setActivationStatus(ORDER_STATUS.FAILED);
        }
      }
    }, 3000);

    setPollingInterval(interval);
  };

  const handleActivate = async () => {
    if (!playerId || playerId.length < 3) {
      setPlayerIdError("Please enter a valid Player ID");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        productId: game?.id,
        receiver: playerId,
      };

      console.log("🔄 Game Activation Payload:", payload);

      const res = await activateGameBundle(payload);
      
      console.log("✅ Game Activation Response:", res);
      
      if (res.success || res.status === 'queued' || res.status === 'processing') {
        setActivationStatus(res.status || ORDER_STATUS.QUEUED);
        setOrderDetails({
          orderId: res.orderId || res.data?.id,
          txnNumber: res.txnNumber || `TXN-${Date.now()}`,
          playerId: playerId,
          amountAfn: game?.price,
          gameName: game?.productName?.en,
          date: new Date().toISOString(),
          message: res.message || "Game activation initiated successfully!",
        });
        
        if (res.orderId || res.data?.id) {
          await startPollingOrderStatus(res.orderId || res.data?.id);
        }
      } else {
        setActivationStatus(ORDER_STATUS.FAILED);
        setOrderDetails({
          playerId: playerId,
          amountAfn: game?.price,
          gameName: game?.productName?.en,
          date: new Date().toISOString(),
          error: res.error || "Activation failed. Please try again.",
        });
      }
    } catch (error) {
      console.error("❌ Failed To Activate Game:", error);
      
      setActivationStatus(ORDER_STATUS.FAILED);
      setOrderDetails({
        playerId: playerId,
        amountAfn: game?.price,
        gameName: game?.productName?.en,
        date: new Date().toISOString(),
        error: error.response?.data?.error || "Activation failed. Please try again.",
      });
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    onClose();
  };

  const getStatusIcon = () => {
    switch (activationStatus) {
      case ORDER_STATUS.QUEUED:
        return <CircularProgress size={24} color="warning" />;
      case ORDER_STATUS.PROCESSING:
      case ORDER_STATUS.PENDING:
        return <CircularProgress size={24} color="primary" />;
      case ORDER_STATUS.SUCCEEDED:
        return <CheckCircleIcon color="success" sx={{ fontSize: 24 }} />;
      case ORDER_STATUS.FAILED:
        return <CheckCircleIcon color="error" sx={{ fontSize: 24 }} />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (activationStatus) {
      case ORDER_STATUS.QUEUED:
        return "warning";
      case ORDER_STATUS.PROCESSING:
      case ORDER_STATUS.PENDING:
        return "primary";
      case ORDER_STATUS.SUCCEEDED:
        return "success";
      case ORDER_STATUS.FAILED:
        return "error";
      default:
        return "info";
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Slide}
      transitionDuration={300}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ color: "#1D2939", fontSize: "20px" }}
        >
          Activate Game
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 1 }}>
        {!activationStatus ? (
          <>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="medium">
                {game?.productName?.en}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {game?.description || "Game credits"}
              </Typography>
              <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                {game?.price} AFN
              </Typography>
            </Box>

            <FormControl fullWidth>
              <TextField
                fullWidth
                placeholder="Enter Player ID"
                value={playerId}
                onChange={handlePlayerIdChange}
                error={!!playerIdError}
                helperText={playerIdError}
                sx={redFocusStyles}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormHelperText>
                Enter the player's game account ID
              </FormHelperText>
            </FormControl>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Box sx={{ mb: 3 }}>
              {getStatusIcon()}
            </Box>
            <Typography variant="h6" color={getStatusColor()} sx={{ mb: 2 }}>
              {activationStatus === ORDER_STATUS.SUCCEEDED 
                ? "Game Activated Successfully!" 
                : activationStatus === ORDER_STATUS.FAILED 
                ? "Activation Failed" 
                : "Processing Activation..."}
            </Typography>
            
            {orderDetails && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Player ID:</strong> {orderDetails.playerId}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Game:</strong> {orderDetails.gameName}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Amount:</strong> {orderDetails.amountAfn} AFN
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Transaction ID:</strong> {orderDetails.txnNumber}
                </Typography>
                {orderDetails.error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {orderDetails.error}
                  </Alert>
                )}
              </Box>
            )}

            {(activationStatus === ORDER_STATUS.QUEUED || 
              activationStatus === ORDER_STATUS.PROCESSING || 
              activationStatus === ORDER_STATUS.PENDING) && (
              <Box sx={{ mt: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {activationStatus === ORDER_STATUS.QUEUED ? 'Queued' : 
                   activationStatus === ORDER_STATUS.PROCESSING ? 'Processing' : 'Finalizing...'}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!activationStatus ? (
          <>
            <Button
              onClick={handleClose}
              variant="outlined"
              color="inherit"
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleActivate}
              variant="contained"
              color="primary"
              disabled={!playerId || !!playerIdError || loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Activating..." : "Activate Game"}
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            variant="contained"
            color="primary"
            fullWidth
          >
            {activationStatus === ORDER_STATUS.SUCCEEDED ? "Done" : "Close"}
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
}